import { Box } from "@material-ui/core"
import ArrowBack from "@material-ui/icons/ArrowBack"
import * as R from "ramda"
import { useEffect, useMemo } from "react"
import type { ReactElement } from "react"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"

import { ChapterOverview } from ".."
import type { MapDto } from "../../../models"
import {
  PageType,
  fetchChapterList,
  fetchLinkChapterList,
  fetchRegionsByChapterId,
  fetchThemesByChapter,
  fetchThemesByRegion,
  indexClearHistory,
  indexInitBreadcrumbs,
  indexPushPage,
  mapFetchByChapter,
  mapFetchByRegion,
  mapFetchByTheme
} from "../../../redux"
import type {
  AppState,
  AppThunkDispatch,
  Breadcrumb,
  Chapter,
  ChapterListAction,
  IndexActions,
  LinkChapter,
  MetadataListAction,
  Project,
  Region,
  RegionListAction,
  Theme,
  ThemeListAction
} from "../../../redux"
import { CardTemplateContainer, Header2, Header4, Header5, LinkButton, LoadingContainer, TitleCard } from "../../atoms"
import { IndexHistory, MapCardContainer } from "../../molecules"

import "./index-viewer.styles.scss"

export interface IIndexViewerOwnProps {
  chapterId?: string
  themeId?: string
  regionId?: string
  projectSelection?: Project[]
  disableLinkChapters?: boolean | undefined
  hideHeaders?: boolean | undefined
  hideBottomSection?: boolean | undefined
  onChapterClicked?: (chapter: Chapter | LinkChapter) => void
  onRegionClicked?: (region: Region) => void
  onThemeClicked?: (theme: Theme) => void
  onMapClicked?: (map: MapDto) => void
  returnToPage?: (page: Breadcrumb) => void
  onMapPreview?: (map: MapDto) => void
}

export interface IIndexViewerStateProps {
  chapters?: Chapter[]
  linkChapters?: LinkChapter[]
  themes?: Theme[]
  regions?: Region[]
  maps?: MapDto[]
  history: Breadcrumb[]
  fetchChapters: () => void
  fetchLinkChapters: () => void
  fetchChapterRegions: (chapterId: string) => void
  fetchChapterThemes: (chapterId: string) => void
  fetchRegionThemes: (regionId: string) => void
  fetchChapterMaps: (chapterId: string) => void
  fetchThemeMaps: (themeId: string) => void
  fetchRegionMaps: (regionId: string) => void
  pushPage: (type: PageType, name: string, id: string) => void
  clearHistory: () => void
  initHistory: () => void
}

function IndexViewerView(props: IIndexViewerStateProps & IIndexViewerOwnProps): ReactElement {
  const {
    chapters,
    linkChapters,
    regions,
    themes,
    maps,
    disableLinkChapters,
    hideHeaders,
    hideBottomSection,
    history,
    fetchChapters,
    fetchLinkChapters,
    fetchChapterRegions,
    fetchChapterThemes,
    fetchChapterMaps,
    fetchRegionThemes,
    fetchRegionMaps,
    fetchThemeMaps,
    pushPage,
    returnToPage,
    clearHistory,
    initHistory,
    chapterId,
    themeId,
    regionId,
    projectSelection,
    onChapterClicked,
    onRegionClicked,
    onThemeClicked,
    onMapClicked,
    onMapPreview
  } = props
  const { t } = useTranslation(["indexPage", "general"])

  const sortedMaps: MapDto[] = useMemo<MapDto[]>(() => {
    if (!maps) return []
    return [...maps].sort((a: MapDto, b: MapDto) => {
      if (a.pageNumber === b.pageNumber) {
        if (!b.pageSection) return 1
        if (!a.pageSection) return -1
        return b.pageSection.toLowerCase() > a.pageSection.toLowerCase() ? -1 : 1
      }
      return a.pageNumber - b.pageNumber
    })
  }, [maps])

  const hasHistory: boolean = useMemo<boolean>(() => {
    return history.length > 0
  }, [history])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    initHistory()
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    setupHistory()
    if (!chapterId && !themeId && !regionId) {
      clearHistory()
      fetchChapters()
      fetchLinkChapters()
    }
    if (chapterId) {
      fetchChapterRegions(chapterId)
      fetchChapterThemes(chapterId)
      fetchChapterMaps(chapterId)
    }
    if (regionId) {
      fetchRegionThemes(regionId)
      fetchRegionMaps(regionId)
    }
    if (themeId) {
      fetchThemeMaps(themeId)
    }
  }, [chapterId, themeId, regionId])

  function showRoot(): boolean {
    return !chapterId && !themeId && !regionId
  }

  function showRegions(): boolean {
    const entityHasRegions: boolean = chapterId !== undefined
    return entityHasRegions && regions !== undefined && regions.length > 0
  }

  function showThemes(): boolean {
    const entityHasThemes: boolean = chapterId !== undefined || regionId !== undefined
    return entityHasThemes && themes !== undefined && themes.length > 0
  }

  function showMaps(): boolean {
    const entityHasMaps: boolean = chapterId !== undefined || regionId !== undefined || themeId !== undefined
    return entityHasMaps && maps !== undefined && maps.length > 0
  }

  function setupHistory(): void {
    if (chapterId && chapters) {
      const chapter: Chapter | undefined = getChapter(chapterId)
      if (!chapter) return
      pushPage(PageType.Chapter, chapter.name, chapterId)
    }
    if (regionId && regions) {
      const region: Region | undefined = getRegion(regionId)
      if (!region) return
      pushPage(PageType.Region, region.name, regionId)
    }
    if (themeId && themes) {
      const theme: Theme | undefined = getTheme(themeId)
      if (!theme) return
      pushPage(PageType.Theme, theme.name, themeId)
    }
  }

  function getChapter(id: string): Chapter | undefined {
    return chapters?.find((chapter: Chapter) => chapter.id === id)
  }

  function getRegion(id: string): Region | undefined {
    return regions?.find((region: Region) => region.id === id)
  }

  function getTheme(id: string): Theme | undefined {
    return themes?.find((theme: Theme) => theme.id === id)
  }

  function navigateToRegion(region: Region): void {
    pushPage(PageType.Region, region.name, region.id as string)
    if (onRegionClicked) onRegionClicked(region)
  }

  function navigateToTheme(theme: Theme): void {
    pushPage(PageType.Theme, theme.name, theme.id as string)
    if (onThemeClicked) onThemeClicked(theme)
  }

  function navigateToMap(map: MapDto): void {
    if (onMapClicked) onMapClicked(map)
  }

  function handleMapPreview(map: MapDto): void {
    if (onMapPreview) onMapPreview(map)
  }

  function pushChapterPage(chapter: Chapter | LinkChapter): void {
    pushPage(PageType.Chapter, chapter.name, chapter.id as string)
    if (onChapterClicked) onChapterClicked(chapter)
  }

  function onReturnToPage(page: Breadcrumb): void {
    if (returnToPage) returnToPage(page)
  }

  function onGoBack(): void {
    if (!returnToPage) return
    if (history.length < 2) {
      returnToPage({ id: "", name: "Index", type: PageType.Index })
      return
    }
    const page: Breadcrumb = history[history.length - 2]
    returnToPage(page)
  }

  return (
    <Box className="index-viewer">
      {hasHistory && (
        <Box className="index-viewer-back-button">
          <LinkButton onClick={onGoBack}>
            <ArrowBack />
            {t("general:goBack")}
          </LinkButton>
          <IndexHistory returnTo={onReturnToPage} />
        </Box>
      )}
      <Box>
        <Box className="index-viewer-content">
          {hasHistory && <Box className="index-viewer-filler" data-mdat-module="index-history" />}
          <Box className={`index-viewer-container ${hasHistory ? "with-history" : ""}`}>
            {!hideHeaders && <Header2>{t("indexPage:indexTitle")}</Header2>}
            {showRoot() && <RootContainer />}
            {!showRoot() && (
              <>
                {showMaps() && <MapContainer />}
                {showRegions() && <RegionContainer />}
                {showThemes() && <ThemeContainer />}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )

  function RootContainer(): ReactElement {
    return (
      <LoadingContainer sections={["chapter"]}>
        {chapters && linkChapters && (
          <Box data-mdat-module="index-root">
            {!hideHeaders && <Header4 className="index-category-title">{t("indexPage:chooseChapter")}</Header4>}
            <ChapterOverview
              onNavigate={pushChapterPage}
              chapters={chapters}
              linkChapters={disableLinkChapters ? [] : linkChapters}
              hideBottom={hideBottomSection}
            />
          </Box>
        )}
      </LoadingContainer>
    )
  }

  function ThemeContainer(): ReactElement {
    // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
    const sortedThemes: Theme[] = useMemo<Theme[]>(() => {
      return themes ? R.sortWith<Theme>([R.ascend(R.prop("order"))])(themes) : []
    }, [themes])

    return (
      <LoadingContainer sections={["theme"]}>
        {sortedThemes && (
          <Box data-mdat-module="index-themes">
            <Header4 className="index-category-title">{t("indexPage:chooseTheme")}</Header4>
            <CardTemplateContainer columns={6}>
              {sortedThemes.map((theme: Theme) => (
                <TitleCard onClick={() => navigateToTheme(theme)} key={theme.id} title={theme.name} data-testid={`theme-card-${theme.id}`} />
              ))}
            </CardTemplateContainer>
          </Box>
        )}
      </LoadingContainer>
    )
  }

  function RegionContainer(): ReactElement {
    // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
    const sortedRegions: Region[] = useMemo<Region[]>(() => {
      return regions ? R.sortWith<Region>([R.ascend(R.prop("order"))])(regions) : []
    }, [regions])

    return (
      <LoadingContainer sections={["region"]}>
        {sortedRegions && (
          <Box data-mdat-module="index-regions">
            <Header4 className="index-category-title">{t("indexPage:chooseArea")}</Header4>
            <CardTemplateContainer columns={6}>
              {sortedRegions.map((region: Region) => (
                <TitleCard onClick={() => navigateToRegion(region)} key={region.id} title={region.name} data-testid={`region-card-${region.id}`} />
              ))}
            </CardTemplateContainer>
          </Box>
        )}
      </LoadingContainer>
    )
  }

  function MapContainer(): ReactElement {
    return (
      <LoadingContainer sections={["map"]}>
        <Box data-mdat-module="index-maps">
          <Header5 className="index-category-title">
            {t("indexPage:chooseMap", {
              mapLength: sortedMaps.length
            })}
          </Header5>
          <MapCardContainer
            big
            onPreview={handleMapPreview}
            onClick={navigateToMap}
            columns={4}
            maps={sortedMaps}
            projectSelection={projectSelection}
            selectable={projectSelection !== undefined}
          />
        </Box>
      </LoadingContainer>
    )
  }
}

function mapStateToProps(state: AppState) {
  return {
    chapters: state.chapters.chapters,
    linkChapters: state.linkChapters.linkChapters,
    regions: state.region.regions,
    themes: state.theme.themes,
    maps: state.maps.maps,
    history: state.index.history
  }
}

function mapDispatchToProps(
  dispatch: AppThunkDispatch<ChapterListAction | RegionListAction | ThemeListAction | MetadataListAction | IndexActions>
) {
  return {
    fetchChapters: () => dispatch(fetchChapterList(0, 100)),
    fetchLinkChapters: () => dispatch(fetchLinkChapterList()),
    fetchChapterRegions: (chapterId: string) => dispatch(fetchRegionsByChapterId(chapterId)),
    fetchChapterThemes: (chapterId: string) => dispatch(fetchThemesByChapter(chapterId)),
    fetchRegionThemes: (regionId: string) => dispatch(fetchThemesByRegion(regionId)),
    fetchChapterMaps: (chapterId: string) => dispatch(mapFetchByChapter(chapterId)),
    fetchRegionMaps: (regionId: string) => dispatch(mapFetchByRegion(regionId)),
    fetchThemeMaps: (themeId: string) => dispatch(mapFetchByTheme(themeId)),
    clearHistory: () => dispatch(indexClearHistory()),
    pushPage: (type: PageType, name: string, id: string) => dispatch(indexPushPage(type, name, id)),
    initHistory: () => dispatch(indexInitBreadcrumbs())
  }
}

export const IndexViewer = connect(mapStateToProps, mapDispatchToProps)(IndexViewerView)
